import Add16 from '@carbon/icons-react/es/add/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import { Upload } from 'antd';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FileUploadStatus } from 'api/attachments';
import { FormThemeImageUploadResponse } from 'api/form';
import { FormThemeImage, FormThemeImageType } from 'api/form/types';
import { Button } from 'components/atoms/button';
import { AttachmentImage } from 'components/molecules/attachment-image';
import {
  useDeleteThemeImageMutation,
  useUploadThemeImageMutation,
} from 'queries/form-template';
import { IMAGE_HEIGHT, IMAGE_WIDTH } from './constants';
import styles from './custom-image.module.css';
import { TickIcon } from './theme-components';

const CustomImageWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-top: var(--spacing-04);
`;
const ThemeTickIcon = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: var(--zindex-layer-01);
`;
const AddLinkButton = styled(Button)`
  svg {
    margin-right: var(--spacing-01);
  }
`;

export const CustomImage = ({
  customImageLocation,
  imageType,
  templateId,
  isSelected,
  setImage,
}: {
  customImageLocation?: string;
  imageType: FormThemeImageType;
  templateId: number;
  isSelected: boolean;
  setImage: (imageUrl: string) => void;
}): JSX.Element => {
  const [customImage, setCustomImage] = useState<FormThemeImage | null>();

  useEffect(() => {
    if (customImageLocation) {
      setCustomImage({
        status: FileUploadStatus.Done,
        location: customImageLocation,
        name: imageType,
      });
    }
  }, [customImageLocation, imageType]);

  const { mutate: uploadThemeImage } = useUploadThemeImageMutation(templateId);

  const { mutateAsync: deleteImage } = useDeleteThemeImageMutation(templateId);

  const handleOnChange = (args) => {
    const { file } = args;

    file.imageId = -1;
    setCustomImage(file);
  };

  const customUploadRequest = (args) => {
    const { file } = args;

    uploadThemeImage(
      {
        file,
        data: {
          name: file.name,
          imageType,
          templateId,
        },
      },
      {
        onSuccess: (data: FormThemeImageUploadResponse) => {
          setCustomImage({
            status: FileUploadStatus.Done,
            location: data.location,
            name: imageType,
          });
        },
      },
    );
  };

  return (
    <CustomImageWrapper>
      {customImage && (
        <div>
          {isSelected && (
            <ThemeTickIcon>
              <TickIcon />
            </ThemeTickIcon>
          )}
          <AttachmentImage
            imageThumbnailClassName={`${styles.custom_image_wrapper}  ${
              isSelected ? styles.custom_image_selected : ''
            }`}
            originFileObj={customImage.originFileObj}
            fileUid="-1"
            fileUploadStatus={customImage.status}
            name={
              imageType === 'HEADER_IMAGE' ? 'Header image' : 'Background image'
            }
            thumbUrl={customImage.location}
            url={customImage.location}
            showCustomPreview={false}
            imageWidth={IMAGE_WIDTH}
            imageHeight={IMAGE_HEIGHT}
            onRemove={() => {
              deleteImage({ imageType });
              setCustomImage(null);
            }}
            onThumbnailClick={() => {
              setImage(customImage?.location);
            }}
            isBasicPreview
          />
        </div>
      )}
      <Upload
        onChange={handleOnChange}
        customRequest={customUploadRequest}
        showUploadList={false}
      >
        <AddLinkButton icon={customImage ? <Edit16 /> : <Add16 />} type="link">
          {customImage ? (
            <FormattedMessage id="form_templates.change_image" />
          ) : (
            <FormattedMessage id="form_templates.add_image" />
          )}
          ...
        </AddLinkButton>
      </Upload>
    </CustomImageWrapper>
  );
};
